import React, { ReactNode } from "react";
import { useFeatures, SalonFeaturesType } from "@/contexts/FeaturesContext";
import { Lock, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLocation } from "wouter";

interface FeatureGateProps {
  feature: keyof SalonFeaturesType;
  children: ReactNode;
  /** Mensagem customizada de bloqueio */
  message?: string;
  /** Nome amigável da funcionalidade */
  featureName?: string;
  /** Plano mínimo necessário */
  requiredPlan?: string;
  /** Modo compacto (inline, sem card) */
  compact?: boolean;
}

const FEATURE_LABELS: Record<string, string> = {
  hasOnlineBooking: "Agendamento Online",
  hasAiChatbot: "Chatbot de IA",
  hasOnlinePayment: "Pagamento Online",
  hasVoiceNotes: "Notas de Voz",
  hasAdvancedReports: "Relatórios Avançados",
  hasCommissions: "Gestão de Comissões",
  hasCheckout: "Caixa / Checkout",
  hasPanelChamada: "Painel de Chamada de Clientes",
  hasMediaPromocional: "Mídia Promocional no Painel TV",
  hasAiWhatsapp: "IA no WhatsApp",
  hasAiInstagram: "IA no Instagram",
  hasBranches: "Gestão de Filiais",
  hasFinancial: "Financeiro",
  hasCrm: "CRM e Retenção",
  hasInventory: "Estoque e Pedidos",
  hasOperations: "Operação e Equipe",
  hasIntelligence: "Inteligência Executiva",
  hasAutomation: "Omnichannel e Automações",
  hasIntegrations: "Integrações",
  hasAcademy: "Academia e Tutorial",
  hasSecurity: "Privacidade e Segurança",
  hasFiscal: "Fiscal",
};

const FEATURE_PLAN: Record<string, string> = {
  hasOnlineBooking: "Essencial",
  hasAiChatbot: "Profissional",
  hasOnlinePayment: "Profissional",
  hasVoiceNotes: "Premium",
  hasAdvancedReports: "Premium",
  hasCommissions: "Profissional",
  hasCheckout: "Essencial",
  hasPanelChamada: "Elite",
  hasMediaPromocional: "Elite",
  hasAiWhatsapp: "Elite",
  hasAiInstagram: "Elite",
  hasBranches: "Elite",
  hasFinancial: "Elite",
  hasCrm: "Elite",
  hasInventory: "Elite",
  hasOperations: "Elite",
  hasIntelligence: "Elite",
  hasAutomation: "Elite",
  hasIntegrations: "Elite",
  hasAcademy: "Elite",
  hasSecurity: "Elite",
  hasFiscal: "Elite",
};

export function FeatureGate({
  feature,
  children,
  message,
  featureName,
  requiredPlan,
  compact = false,
}: FeatureGateProps) {
  const { hasFeature, isLoading } = useFeatures();
  const [, navigate] = useLocation();

  // Enquanto carrega, renderiza normalmente para evitar flash
  if (isLoading) return <>{children}</>;

  // Feature liberada: renderiza normalmente
  if (hasFeature(feature)) return <>{children}</>;

  const label = featureName ?? FEATURE_LABELS[feature] ?? feature;
  const plan = requiredPlan ?? FEATURE_PLAN[feature] ?? "superior";
  const defaultMessage = `Esta funcionalidade (${label}) está disponível no plano ${plan} ou superior.`;

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-dashed text-muted-foreground text-sm">
        <Lock className="h-4 w-4 shrink-0" />
        <span>{message ?? defaultMessage}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[300px] p-6">
      <Card className="max-w-md w-full border-dashed">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-muted">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{label}</h3>
            <p className="text-sm text-muted-foreground">
              {message ?? defaultMessage}
            </p>
          </div>
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-3">
              Faça upgrade do seu plano para desbloquear esta funcionalidade.
            </p>
            <Button
              size="sm"
              className="gap-2"
              onClick={() => navigate("/upgrade")}
            >
              <Crown className="h-4 w-4" />
              Ver Planos e Fazer Upgrade
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
